import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as ChargingStationIcon } from '../../../assets/icons/sidebar/sidebar-station.svg';
import { AvailableTabProperties } from '../../../interface/location-management-side-panel.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setSelectedStation } from '../../../redux/slices/LocationManagementSidePanelSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';

import LocationComparison from './components/LocationComparison';
import LocationDashboard from './components/LocationDashboard';
import LocationManagementTabs from './components/LocationManagementTabs';
import LocationPerformance from './components/LocationPerformance';
import LocationReports from './components/LocationReports';
import LocationTickets from './components/LocationTickets';

/**
 * Station Management
 * @returns {any} The rendered component
 */
function StationManagementSidePanel() {
  const { i18n } = useTranslation('sidePanel');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tab: deviceTab } = useParams<{ tab: string }>();
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const { id } = useParams();
  const [searchParameters] = useSearchParams();
  const { t } = useTranslation('location');

  const searchBy = searchParameters.get('searchBy'); // "New station"
  const [containerTabs, setContainerTabs] = useState<AvailableTabProperties[]>([
    {
      label: t('stationDashboard'),
      key: 'location-dashboard',
      isActive: false,
    },
    {
      label: t('stationPerformance'),
      key: 'location-performance',
      isActive: false,
    },
    {
      label: t('reports'),
      key: 'reports',
      isActive: false,
    },
    {
      label: t('stationComparison'),
      key: 'location-comparison',
      isActive: false,
    },
    // {
    //   label: t('tickets'),
    //   key: 'tickets',
    //   isActive: false,
    // },
  ]);

  useEffect(() => {
    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        let { label } = tab;
        if (tab.key === 'location-dashboard') label = t('stationDashboard');
        if (tab.key === 'location-performance') label = t('stationPerformance');
        if (tab.key === 'reports') label = t('reports');
        if (tab.key === 'location-comparison') label = t('stationComparison');
        if (tab.key === 'tickets') label = t('tickets');
        return {
          ...tab,
          label,
        };
      }),
    );
  }, [t]);

  /**
   * Function to switch the tab
   * @param {string} key - The tab key
   * @param {boolean} navigateRequire - Whether navigation is required
   */
  const handleSwitchTab = (key: string, navigateRequire: boolean = true) => {
    const normalizedKey = key ? key.replace(/^station-/, 'location-') : key;
    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === normalizedKey) {
          if (navigateRequire) {
            const isExpanded =
              location.pathname.includes('location-expanded') ||
              location.pathname.includes('station-expanded');
            const isStationPath =
              location.pathname.includes('station-expanded') ||
              location.pathname.includes('station-management');
            let basePath = 'location-management';
            if (isExpanded) {
              basePath = isStationPath ? 'station-expanded' : 'location-expanded';
            } else if (isStationPath) {
              basePath = 'station-management';
            }
            navigate(
              {
                pathname: `/ev-admin/${basePath}/${selectedChargingPoint?.id}/${tab.key}`,
                search: `?searchBy=${selectedChargingPoint?.name}`,
              },
              {
                state: {
                  source:
                    location.state?.source ||
                    new URLSearchParams(location.search).get('source') ||
                    location.pathname,
                },
              },
            );
          }

          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  useEffect(() => {
    handleSwitchTab(deviceTab || 'location-dashboard', false);
  }, [deviceTab]);

  useEffect(() => {
    if (!id) return;
    if (selectedChargingPoint?.id === id) return;
    dispatch(
      setSelectedStation({
        id,
        name: searchBy,
      }),
    );
  }, [id]);
  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('stationManagement', 'Station Management'),
        icon: <ChargingStationIcon />,
      }),
    );
  }, [t]);

  return (
    <div className="location-management-side-panel-wrap" dir={i18n.dir()}>
      <div className="location-management-container">
        <LocationManagementTabs containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
        <div className="location-management-content">
          {(() => {
            const activeTab = containerTabs.find((tab) => tab.isActive)?.key || 'device-details';

            let deviceContent = null;
            switch (activeTab) {
              case 'location-dashboard':
              case 'station-dashboard':
                deviceContent = <LocationDashboard />;
                break;
              case 'location-performance':
              case 'station-performance':
                deviceContent = <LocationPerformance />;
                break;
              case 'reports':
                deviceContent = <LocationReports />;
                break;
              case 'location-comparison':
              case 'station-comparison':
                deviceContent = <LocationComparison />;
                break;
              case 'tickets':
                deviceContent = <LocationTickets />;
                break;
              default:
                deviceContent = <LocationDashboard />;
                break;
            }
            return deviceContent;
          })()}
        </div>
      </div>
    </div>
  );
}

export default StationManagementSidePanel;
